import type { ZodArray } from "zod";
import * as z from "zod";
import type { CategoryInfo } from "../types/categoryTypes";
import {
   type AssortmentEntry,
   type Filters,
   legalFilterKeys,
   type ProductAvailability,
   type RawProduct
} from "../types/productTypes";

export const ZNutritionLineType = z.object({
   name: z.string(),
   value: z.string()
});

export const ZProductNutrition = z.object({
   productName: z.string(),
   productNutritions: z.array(ZNutritionLineType)
});
export const ZProductNutritions = z.array(ZNutritionLineType).or(z.array(ZProductNutrition));

export const ZEan = z.object({
   sku: z.string(),
   code: z.string(),
   packaging: z.string()
});

export const ZOrderUnit = z.enum(["D", "STK", "L", "K", "P", "CS", "KG", "DFP", "TNK", "PCS"]);
export const ZCategoryReference = z.object({
   externalReference: z.string(),
   name: z.string()
});
export const ZCoreProduct = z.object({
   sku: z.string(),
   name: z.string(),
   buyableInBaseUnit: z.boolean(),
   buyableInSalesUnit: z.boolean(),
   baseUnit: ZOrderUnit,
   salesUnit: ZOrderUnit,
   weight: z.number(),
   variableWeight: z.boolean(),
   conversionFactor: z.number().nullable(),
   comparativeUnit: z.string().nullable(),
   epdNumber: z.string().nullable(),
   description: z.string().nullish(),
   eans: z.array(ZEan).nullish(),
   preservable: z.string().nullable(),
   type: z.string().nullish(),
   similarProducts: z.array(z.string()),
   replacementProducts: z.array(z.string()).optional(),
   newUntilDate: z.string().nullable(),
   expiringFromDate: z.string().nullish(),
   objectID: z.string(),
   brand: z.string().nullable(),
   brands: z.array(z.string()).nullish(),
   certifications: z.array(z.string()).nullable(),
   countryOfOrigin: z.string().nullable(),
   searchWord: z.array(z.string()),
   nutritions: ZProductNutritions.nullish(),
   nutritionBaseUnit: z.string().optional(),
   allergens: z.string().nullable(),
   ingredients: z.string().nullable(),
   packaging: z.string().nullable(),
   storage: z.string().nullable(),
   recyclingChargeBaseUnit: z.number(),
   leadTime: z.string().nullable(),
   miniTank: z.boolean(),
   url_key: z.string(),
   size: z.string().nullish(),
   sizegroup: z.string().optional(),
   taste: z.string().nullish(),
   customertype: z.string().optional(),
   parentCategory: ZCategoryReference.optional(),
   category: ZCategoryReference.optional(),
   accessorytype: z.string().optional(),
   expired: z.boolean().optional(),
   weightrange: z.string().nullish(),
   volumerange: z.string().nullish(),
   sizerange: z.string().nullish(),
   tastes: z.array(z.string()).optional(),
   tastegroups: z.array(z.string()).optional(),
   allergyinfo: z.array(z.string()).optional(),
   format: z.string().nullish(),
   manuallabels: z.array(z.string()).optional(),
   manualshortcuts: z.array(z.string()).optional()
}) satisfies z.ZodType<RawProduct>;

export const ZCategoryInfo = z.object({
   id: z.string().optional(),
   name: z.string(),
   fullUrl: z.string(),
   url_key: z.string(),
   skus: z.array(z.string()),
   get children(): ZodArray<z.ZodType<CategoryInfo>> {
      return z.array(ZCategoryInfo);
   },
   visible: z.boolean().optional(),
   catId: z.string().optional()
}) satisfies z.ZodType<CategoryInfo>;

export const ZCategoryInfos = z.array(ZCategoryInfo);

export const ZLegalFilterKeyEnum = z.enum(legalFilterKeys);

export const ZFilters = z.partialRecord(ZLegalFilterKeyEnum, z.array(z.string())) satisfies z.ZodType<Filters>;

export const ZProductsCategoriesMetadata = z.object({
   products: z.array(ZCoreProduct),
   categories: z.array(ZCategoryInfo),
   metadata: z.object({
      options: ZFilters
   })
});

export const ZAssortmentEntry = z.object({
   itemId: z.string(),
   inAgreementAssortment: z.boolean(),
   preferredProduct: z.boolean().nullish(),
   dpakPrice: z.number().nullable(),
   dpakPriceBeforeDiscount: z.number().nullable(),
   dpakPerUnitPrice: z.number().nullish(),
   dpakPerUnitPriceBeforeDiscount: z.number().nullish(),
   fpakPrice: z.number().nullish(),
   fpakPriceBeforeDiscount: z.number().nullish(),
   comparisonPriceUnit: z.string().nullable(),
   comparisonPrice: z.number().nullable(),
   promotionalProduct: z.boolean().optional(),
   canBuy: z.boolean().optional(),
   VAT: z.number().optional(),
   allowedOrderLineUnits: z.array(z.string()).optional(),
   hasPromotion: z.boolean().optional()
}) satisfies z.ZodType<AssortmentEntry>;

export const ZAssortmentEntries = z.array(ZAssortmentEntry);

export const ZProductAvailability = z.object({
   productNumber: z.string(),
   plannedAvailableDate: z.string().nullable(),
   delayedDeliveryDate: z.string().nullable(),
   availableQuantity: z.number(),
   customerWarehouse: z.string().nullable(),
   warehouse: z.string().nullable(),
   available: z.boolean(),
   partialAvailable: z.boolean(),
   supplyingWarehouse: z.string(),
   atCustomersWarehouse: z.boolean()
}) satisfies z.ZodType<ProductAvailability>;

export const ZProductAvailabilities = z.array(ZProductAvailability);
